import { useState, useRef, useCallback } from "react";
import { getAuthHeaders } from "../../auth/services/getAuthHeaders";

// ── Types ──────────────────────────────────────────────────────────────────────

interface LangData {
  summary: string;
  html_filename: string;
  s3_url: string;
}

export interface BilingualData {
  es?: LangData;
  en?: LangData;
}

interface UseSummaryGeneratorProps {
  summaryServiceUrl: string;
  currentMeetingId: string | null;
  targetLang: string;
  targetLangLabel: string;
  meetingTranscript: { timestamp: string; orig: string; tran: string }[];
  duration?: string;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useSummaryGenerator({
  summaryServiceUrl,
  currentMeetingId,
  targetLang,
  targetLangLabel,
  meetingTranscript,
  duration = "",
}: UseSummaryGeneratorProps) {
  const [summaryText, setSummaryText] = useState("");
  const [s3Url, setS3Url] = useState("");
  const [htmlFilename, setHtmlFilename] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPolling, setIsPolling] = useState(false);
  const [backendDuration, setBackendDuration] = useState<string | null>(null);
  const [transcriptCount, setTranscriptCount] = useState<number | null>(null);

  // Bilingual state
  const [bilingualData, setBilingualData] = useState<BilingualData | null>(null);
  const [activeLang, setActiveLangState] = useState<"es" | "en">("es");

  const fetchInProgress = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref for use inside setInterval callbacks without stale closures
  const bilingualDataRef = useRef<BilingualData | null>(null);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /** Picks "es" or "en" from targetLang (defaults to "es") */
  const getDefaultLang = (): "es" | "en" => {
    const code = targetLang.split("-")[0]?.toLowerCase();
    return code === "en" ? "en" : "es";
  };

  /** Applies a specific language slice from bilingual data into the display states */
  const applyLangData = useCallback((lang: "es" | "en", data: BilingualData) => {
    const slice = data[lang];
    if (!slice) return;
    setSummaryText(slice.summary || "No se recibió respuesta del resumen.");
    setS3Url(slice.s3_url || "");
    setHtmlFilename(slice.html_filename || "");
  }, []);

  /**
   * Parses a completed API response and updates all relevant states.
   * Handles both formats:
   *   • New (Cora): { data: { es: {...}, en: {...} }, duration, transcript_count }
   *   • Old (Kira): { summary, s3_url, html_filename, duration, transcript_count }
   */
  const handleCompletedData = useCallback(
    (data: any) => {
      if (data.duration) setBackendDuration(data.duration);
      if (typeof data.transcript_count === "number") setTranscriptCount(data.transcript_count);

      // New bilingual format
      if (data.data && (data.data.es || data.data.en)) {
        const bil: BilingualData = { es: data.data.es, en: data.data.en };
        bilingualDataRef.current = bil;
        setBilingualData(bil);
        const lang = getDefaultLang();
        setActiveLangState(lang);
        applyLangData(lang, bil);
      } else {
        // Old monolingual format — clear bilingual state
        bilingualDataRef.current = null;
        setBilingualData(null);
        setSummaryText(data.summary || "No se recibió respuesta del resumen.");
        setS3Url(data.s3_url || "");
        setHtmlFilename(data.html_filename || "");
      }
    },
    [applyLangData, targetLang] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Public API ───────────────────────────────────────────────────────────────

  /** Switches the displayed language (only effective when bilingual data is available) */
  const switchLang = useCallback(
    (lang: "es" | "en") => {
      setActiveLangState(lang);
      if (bilingualDataRef.current) {
        applyLangData(lang, bilingualDataRef.current);
      }
    },
    [applyLangData]
  );

  const resetGeneration = useCallback(() => {
    setSummaryText("");
    setS3Url("");
    setHtmlFilename("");
    setError("");
    setIsPolling(false);
    setBackendDuration(null);
    setTranscriptCount(null);
    setBilingualData(null);
    setActiveLangState("es");
    bilingualDataRef.current = null;
    fetchInProgress.current = false;
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
  }, []);

  const setSavedSummary = useCallback((text: string) => {
    setSummaryText(text);
    // Saved summaries are always monolingual — clear bilingual toggle
    setBilingualData(null);
    bilingualDataRef.current = null;
    setLoading(false);
  }, []);

  // ── Polling ──────────────────────────────────────────────────────────────────

  const pollStatus = async (meetingId: string | number) => {
    setIsPolling(true);
    setLoading(true);
    setError("");

    pollIntervalRef.current = setInterval(async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const res = await fetch(`${summaryServiceUrl}/summarize/${meetingId}/status`, {
          headers: authHeaders,
        });
        if (!res.ok) return;

        const data = await res.json();

        if (data.status === "completed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          handleCompletedData(data);
          setIsPolling(false);
          setLoading(false);
        } else if (data.status === "failed") {
          clearInterval(pollIntervalRef.current!);
          if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
          setError(data.error || "Fallo interno al generar la minuta.");
          setIsPolling(false);
          setLoading(false);
        }
      } catch (err) {
        console.warn("Error consultando el status de la minuta", err);
      }
    }, 5000);

    safetyTimeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        setError("Tiempo de espera agotado. La minuta tardó más de 15 minutos.");
        setIsPolling(false);
        setLoading(false);
      }
    }, 15 * 60 * 1000);
  };

  // ── Generate ─────────────────────────────────────────────────────────────────

  const compileTranscriptText = () =>
    meetingTranscript
      .map((t) => `[${t.timestamp}] Origen: "${t.orig}" | Traducción: "${t.tran}"`)
      .join("\n");

  const generateSummary = async () => {
    if (fetchInProgress.current) return;
    fetchInProgress.current = true;
    setLoading(true);
    setError("");

    const transcriptText = compileTranscriptText();

    try {
      const authHeaders = await getAuthHeaders();
      const outputLangCode = targetLang.split("-")[0]?.toLowerCase() || "es";
      const response = await fetch(`${summaryServiceUrl}/summarize`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          transcript: transcriptText,
          meeting_id: currentMeetingId,
          format_type: "minuta",
          output_language: outputLangCode,
          target_lang: targetLang,
          duration: duration || undefined,
        }),
      });

      if (!response.ok) {
        if (response.status === 504 || response.status === 503 || response.status === 502) {
          if (currentMeetingId) pollStatus(currentMeetingId);
          return;
        }

        let errorMsg = `Error del servidor: ${response.status} ${response.statusText}`;
        try {
          const errData = await response.json();
          if (errData.code === "TIMEOUT") errorMsg = "El servidor tardó demasiado";
          else if (errData.code === "EMPTY_TRANSCRIPT") errorMsg = "No se capturaron suficientes segmentos";
          else if (errData.code === "MODEL_UNAVAILABLE") errorMsg = "El servicio de IA no está disponible";
          else if (errData.error) errorMsg = errData.error;
        } catch (_e) {
          // Ignorar si no es JSON
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();

      // Detect if we got content directly (old or new format) or need polling
      const hasContent =
        data.data?.es?.summary || data.data?.en?.summary || data.summary;
      if (!hasContent && currentMeetingId) {
        pollStatus(currentMeetingId);
        return;
      }

      handleCompletedData(data);
    } catch (err: any) {
      if (
        err.message?.includes("fetch") ||
        err.message?.includes("Network") ||
        err.message?.includes("Failed")
      ) {
        if (currentMeetingId) {
          pollStatus(currentMeetingId);
          return;
        }
      }
      console.error(err);
      setError(err.message || "Error al conectar con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  // ── Return ───────────────────────────────────────────────────────────────────

  return {
    summaryText,
    s3Url,
    htmlFilename,
    loading,
    error,
    isPolling,
    backendDuration,
    transcriptCount,
    bilingualData,
    activeLang,
    switchLang,
    generateSummary,
    resetGeneration,
    setSavedSummary,
    setError,
  };
}
